import React, { useEffect, useState } from "react";
import {
  Grid,
  Box,
  MenuItem,
  Menu,
  Avatar,
  Typography,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  useMediaQuery,
} from "@mui/material";
import {
  SettingsOutlined,
  LockReset,
} from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import useAuth from "./../hooks/useAuth";
import colors from "./../styles/colors";
import { PrimaryButton } from "./../components/Button/Button";
import { SuccessToaster, ErrorToaster } from "./../components/Toaster/Toaster";
import AuthServices from "./../apis/Auth/AuthServices";
import ProfileServices from "../apis/Profile/ProfileServices";
import { baseUrl } from "../../axios";
import ResetPasswordDialog from "../components/Dialog/ResetPasswordDialog";

// * Notification badge style
const StyledBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    right: 18,
    top: 6,
    border: `1px solid ${colors.white}`,
  },
}));

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { userLogout } = useAuth();
  const navigate = useNavigate();

  const [quoteDialog, setQuoteDialog] = useState(false);
  const [profile, setProfile] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);
  const handleClick = (event) =>
    setAnchorEl(anchorEl ? null : event.currentTarget);

  const logout = async () => {
    const fcmToken = localStorage.getItem("fcmToken");
    try {
      const { data } = await AuthServices.logout({ fcm_token: fcmToken });
      if (data === null) return;
      userLogout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const resetPassword = async (formData, reset) => {
    try {
      const { data, message } = await AuthServices.reset({
        oldPassword: formData.oldPass,
        newPassword: formData.newPass,
      });
      if (data === null) return;
      SuccessToaster(message);
      reset();
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const getProfile = async () => {
    try {
      const { data } = await ProfileServices.getProfile();
      if (data === null) return;
      setProfile(data.result);
    } catch (error) {
      ErrorToaster(error);
      console.error("Get profile error:", error);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  return (
    <AppBar
      position="sticky"
      sx={{
        bgcolor: colors.white,
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        px: { xs: 2, sm: 4, md: 6 },
      }}
    >
      <ResetPasswordDialog
        open={quoteDialog}
        onClose={() => setQuoteDialog(!quoteDialog)}
        title="Reset Password"
        action={resetPassword}
      />

      <Toolbar sx={{ minHeight: { xs: 60, sm: 70 } }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          flexWrap="nowrap"
        >
          {/* Left Section — Can hold logo later */}
          <Grid item xs={6} sm={3} sx={{ml:{md:0,xs:3}}}>
            <Typography
              variant="h6"
              sx={{
                color: colors.primary,
                fontWeight: 700,
                fontSize: { xs: "1.1rem", sm: "1.3rem" },
              }}
            >
              Dashboard
            </Typography>
          </Grid>

          {/* Right Section */}
          <Grid
            item
            xs={6}
            sm="auto"
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: { xs: 1.5, sm: 2 },
            }}
          >
          <Box
                onClick={handleClick}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: isMobile ? 0.5 : 1,
                  borderRadius: "22px",
                  backgroundColor: open
                    ? colors.primary
                    : colors.lightGray,
                  "&:hover": {
                    backgroundColor: colors.primary,
                    cursor: "pointer",
                    ".MuiSvgIcon-root": { color: colors.cloud },
                  },
                }}
              >
                <Avatar
                  alt="profile"
                  src={baseUrl + profile.picture}
                  sx={{ width: 30, height: 30 }}
                />
                {!isMobile && (
                  <SettingsOutlined
                    sx={{
                      color: open
                        ? colors.lightGray
                        : colors.secondary,
                      fontSize: "26px",
                      ml: 2,
                    }}
                  />
                )}
              </Box>
          </Grid>
        </Grid>

        {/* Dropdown Menu */}
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              width: { xs: "85vw", sm: 320 },
              px: 2,
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 56,
                height: 56,
                mr: 1.5,
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: colors.black,
              py: 1,
            }}
          >
            User Profile
          </Typography>

          {/* Profile Info */}
          <Box
            onClick={() => navigate("/profile")}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              p: 1,
              borderRadius: "8px",
              "&:hover": { bgcolor: colors.lightGray },
            }}
          >
            <Avatar src={baseUrl + profile?.picture} />
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>
                {profile?.name}
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", color: "#555" }}>
                {profile?.email}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Change Password */}
          <MenuItem
            sx={{ p: 1, borderRadius: "8px" }}
            onClick={() => setQuoteDialog(!quoteDialog)}
          >
            <LockReset sx={{ mr: 2, fontSize: "22px", color: colors.primary }} />
            <Typography>Change Password</Typography>
          </MenuItem>

          {/* Logout Button */}
          <Box sx={{ my: 1.5 }}>
            <PrimaryButton onClick={logout} title="Logout" fullWidth />
          </Box>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
